// BulkUploadModal.tsx - FULLY REVAMPED UI

import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import * as actions from '../../../../Redux/Actions/Action';
import { showErrorToastMessage, showSuccessToastMessage } from '../../../../component/Toasts/Toasts';
import { Button, ProgressBar } from 'react-bootstrap';
import * as XLSX from "xlsx";
import { FiUploadCloud, FiFileText, FiCheckCircle, FiXCircle, FiAlertTriangle } from 'react-icons/fi';
import './BulkUploadModal.scss'; // We will create this SCSS file

// Interfaces remain the same
interface IUploadResults {
  successCount: number;
  failureCount: number;
  faultyRecords: any[];
}
interface BulkUploadModalProps {
  onHide: () => void;
  onUploadSuccess: () => void;
}

export default function BulkUploadModal({ onHide, onUploadSuccess }: BulkUploadModalProps) {
  const dispatch = useDispatch();
  const [status, setStatus] = useState<'idle' | 'uploading' | 'processing' | 'completed' | 'error'>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [logId, setLogId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [results, setResults] = useState<IUploadResults | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  
  useEffect(() => {
    if (status !== 'processing' || !logId) return;

    const intervalId = setInterval(() => {
      dispatch(actions.getEmployeeBulkUploadStatusAction(logId, (res: any) => {
        if (res.code === 200 && res.data) {
          const log = res.data;
          const calculatedProgress = log.totalRecords > 0 ? Math.round((log.processedRecords / log.totalRecords) * 100) : (log.status === 'PROCESSING' ? 10 : 0);
          setProgress(calculatedProgress);

          if (log.status === 'PROCESSING') {
            setStatusMessage(`${log.processedRecords} of ${log.totalRecords} records processed...`);
          }

          if (log.status === 'COMPLETED' || log.status === 'FAILED') {
            if (log.results?.successCount > 0) {
              onUploadSuccess(); 
            }
            setStatus(log.status === 'COMPLETED' ? 'completed' : 'error');
            setResults({
              successCount: log.results?.successCount || 0,
              failureCount: log.results?.failureCount || 0,
              faultyRecords: log.faultyRecords || [],
            });
          }
        } else {
          setStatus('error');
        }
      }));
    }, 3000);

    return () => clearInterval(intervalId);
  }, [status, logId, dispatch, onUploadSuccess]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setSelectedFile(event.target.files[0]);
    }
  };

  const handleSubmit = () => {
    if (!selectedFile) {
      showErrorToastMessage("Please select a file to upload.");
      return;
    }
    setStatus('uploading');
    const formData = new FormData();
    formData.append('file', selectedFile);
    dispatch(actions.startEmployeeBulkUploadAction(formData, (res: any) => {
      if ((res.success || (res.code >= 200 && res.code < 300)) && res.data?.logId) {
        setLogId(res.data.logId);
        setStatus('processing');
        setStatusMessage('File accepted. Starting process...');
      } else {
        showErrorToastMessage(res.error?.error_description || "Failed to start upload process.");
        setStatus('error');
      }
    }));
  };

  const generateFaultyReport = () => {
    if (results && results.faultyRecords.length > 0) {
      const worksheet = XLSX.utils.json_to_sheet(results.faultyRecords);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Faulty Records");
      XLSX.writeFile(workbook, "Fault_Records_Report.xlsx");
      showSuccessToastMessage("Faulty records report downloaded.");
    }
  };

  // --- NEW: A function to reset the state to allow another upload ---
  const handleReset = () => {
    setStatus('idle');
    setSelectedFile(null);
    setLogId(null);
    setProgress(0);
    setStatusMessage('');
    setResults(null);
    if (fileInputRef.current) {
        fileInputRef.current.value = ""; // Clear the file input
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'idle':
      case 'uploading':
        return (
          <>
            <div className="upload-area" onClick={() => fileInputRef.current?.click()}>
              <input type="file" accept=".csv,.xlsx" onChange={handleFileChange} ref={fileInputRef} style={{ display: 'none' }} />
              <FiUploadCloud className="upload-icon" />
              <p>Click to browse your file here</p>
              <small>.csv files only</small>
            </div>
            {selectedFile && (
              <div className="file-info">
                <FiFileText />
                <span>{selectedFile.name}</span>
              </div>
            )}
            <div className="modal-actions">
                <Button variant="outline-secondary" onClick={onHide}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={!selectedFile || status === 'uploading'}>
                    {status === 'uploading' ? 'Uploading...' : 'Upload & Process'}
                </Button>
            </div>
          </>
        );

      case 'processing':
        return (
          <div className="processing-container">
            <h4>Processing...</h4>
            <p>Please do not close this window. This may take a few moments.</p>
            <ProgressBar now={progress} animated striped label={`${progress}%`} className="mt-3 mb-3" />
            <p className="status-message">{statusMessage}</p>
          </div>
        );

      case 'completed':
      case 'error':
        const successCount = results?.successCount || 0;
        const failureCount = results?.failureCount || 0;
        const isError = status === 'error' && failureCount === 0; // Handle API errors vs processing errors
        
        return (
          <div className="results-container">
            <h4>{isError ? 'Upload Failed' : 'Process Complete'}</h4>
            {isError ? (
                <div className="result-item error">
                    <FiAlertTriangle className="icon" />
                    <span>The upload process could not be completed due to a server error. Please try again.</span>
                </div>
            ) : (
                <div className="results-summary">
                    <div className="result-item success">
                        <FiCheckCircle className="icon" />
                        <span><strong>{successCount}</strong> records processed successfully.</span>
                    </div>
                    <div className={`result-item ${failureCount > 0 ? 'failure' : ''}`}>
                        <FiXCircle className="icon" />
                        <span><strong>{failureCount}</strong> records failed.</span>
                    </div>
                </div>
            )}
            <div className="modal-actions">
              <Button variant="outline-secondary" onClick={handleReset}>Upload Another File</Button>
              {failureCount > 0 && (
                <Button variant="warning" onClick={generateFaultyReport}>Download Error Report</Button>
              )}
              <Button variant="primary" onClick={onHide}>Done</Button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bulk-upload-modal">
      {renderContent()}
    </div>
  );
}